<template>
  <div id="dt-viewer-app" class="decision-tree-viewer-app" :class="classModifiers">
    <View class="dt-viewer-app__view-app-loading" v-if="loading" :preview-mode="previewMode">
      <sp-animated-ellipsis></sp-animated-ellipsis>
    </View>
    <DecisionTreeViewerPreviewView v-else-if="previewMode" />
    <DecisionTreeViewerView v-else />
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { useMediaQuery } from "@vueuse/core";

import View from "./components/View.vue";
import DecisionTreeViewerPreviewView from "./views/DecisionTreeViewerPreviewView.vue";
import DecisionTreeViewerView from "./views/DecisionTreeViewerView.vue";

import { useStateStore } from "./store/state";
import { useVisitTypeStore } from "./store/visit-type";
import { useDecisionTreeStore } from "./store/decision-tree";

const { load: loadStates } = useStateStore();
const { load: loadVisitTypes } = useVisitTypeStore();
const { previewMode, load: loadDecisionTree, start: startDecisionTree } = useDecisionTreeStore();

const loading = ref(true);

const xsBreakpoint = computed(() =>
  window.getComputedStyle(document.documentElement).getPropertyValue("--sp-ref-breakpoint-xs"),
);

const isLteXsScreen = useMediaQuery(`(max-width: ${xsBreakpoint.value})`);
const classModifiers = computed(() => ({
  "--bp-lte-xs-screen": isLteXsScreen.value,
}));

onMounted(async () => {
  loading.value = true;

  try {
    await Promise.all([loadStates(), loadVisitTypes(), loadDecisionTree()]);

    if (!previewMode) {
      startDecisionTree();
    }
  } finally {
    loading.value = false;
  }
});
</script>

<style scoped lang="scss">
.decision-tree-viewer-app {
  --dtv-font-size: var(--sp-sys-body-font-size, 1rem);
  font-size: var(--dtv-font-size);
  margin-bottom: var(--sp-dtv-app-margin-bottom, 4rem);
  --dtv-app-content-min-height: var(
    --sp-dtv-content-min-height,
    clamp(20rem, 42vh, 31.25rem)
  ); // is needed for View and Card components because of the slide transition/animation

  // mobile last :-( - TODO @mgruschetzki: refactor to mobile first
  &.--bp-lte-xs-screen {
    // <View>
    --dtv-view-padding-block: var(--sp-dtv-view-padding-block-mobile, 0.5rem);
    --dtv-view-fade-out-pseudo-slides-display: var(--sp-dtv-view-fade-out-pseudo-slides-display-mobile, none);

    // <Card>
    --dtv-card-actions-grid-columns: var(--sp-dtv-card-actions-grid-columns-mobile, 1fr);
    --dtv-card-actions-center-grid-row: var(--sp-dtv-card-actions-center-grid-row-mobile, 2);
    --dtv-card-actions-end-grid-row: var(--sp-dtv-card-actions-end-grid-row-mobile, 3);
    --dtv-card-border: var(--sp-dtv-card-border-mobile, none);
    --dtv-card-box-shadow: var(--sp-dtv-card-box-shadow-mobile, none);
    --dtv-card-padding-block: var(--sp-dtv-card-padding-block-mobile, 0);
    --dtv-card-padding-inline: var(--sp-dtv-card-padding-inline-mobile, 0);

    // <Slide>
    --dtv-slide-nav-btn-margin-top: 1rem;
    --dtv-slide-nav-btn-min-width: var(--sp-dtv-slide-nav-btn-min-width-mobile, 100%);

    // <Node>
    --dtv-node-title-font-size: var(--sp-dtv-node-title-font-size-mobile, 1.125rem);
  }
}

.dt-viewer-app__view-app-loading {
  align-items: center;
  margin-inline: auto;

  sp-animated-ellipsis {
    --color: var(--sp-dtv-animated-ellipsis-color, #00818a);
  }
}
</style>
